import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Save, FileText, Shield, ScrollText, Cookie } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

const LEGAL_PAGES = [
  { key: 'privacy', id: 'privacy-policy', title: 'Privacy Policy', titleDe: 'Datenschutzrichtlinie', icon: Shield },
  { key: 'terms', id: 'terms-of-service', title: 'Terms of Service', titleDe: 'Nutzungsbedingungen', icon: ScrollText },
  { key: 'cookies', id: 'cookie-policy', title: 'Cookie Policy', titleDe: 'Cookie-Richtlinie', icon: Cookie }
];

export default function LegalManager() {
  const [content, setContent] = useState({});
  const [titles, setTitles] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeDoc, setActiveDoc] = useState('privacy');
  const [activeLang, setActiveLang] = useState('en');

  useEffect(() => { fetchContent(); fetchTitles(); }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/legal-content`);
      if (response.data) {
        setContent(response.data);
      }
    } catch (error) {
      console.error('Error fetching legal content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTitles = async () => {
    try {
      const response = await axios.get(`${API}/admin/legal`);
      if (response.data?.pages) {
        const titlesMap = {};
        response.data.pages.forEach(page => {
          const key = page.id === 'privacy-policy' ? 'privacy' : 
                      page.id === 'terms-of-service' ? 'terms' : 'cookies';
          titlesMap[`${key}_title_en`] = page.title_en || '';
          titlesMap[`${key}_title_bg`] = page.title_bg || '';
          titlesMap[`${key}_title_de`] = page.title_de || '';
        });
        setTitles(titlesMap);
      }
    } catch (error) {
      console.error('Error fetching titles:', error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Combine content and titles into one request
      const payload = { ...content, ...titles };
      await axios.put(`${API}/admin/legal-content`, payload);
      toast.success('Legal content saved!');
    } catch (error) {
      toast.error('Error saving content');
    } finally {
      setIsSaving(false);
    }
  };

  const updateContent = (docType, lang, value) => {
    setContent(prev => ({
      ...prev,
      [`${docType}_${lang}`]: value
    }));
  };

  const updateTitle = (docType, lang, value) => {
    setTitles(prev => ({
      ...prev,
      [`${docType}_title_${lang}`]: value
    }));
  };

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Legal Documents</h2>
          <p className="text-sm text-slate-500">Manage privacy policy, terms of service, and cookie policy</p>
        </div>
        <Button onClick={handleSave} disabled={isSaving} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
          <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      {/* Document Type Tabs */}
      <Tabs value={activeDoc} onValueChange={setActiveDoc}>
        <TabsList className="grid w-full grid-cols-3 mb-4">
          {LEGAL_PAGES.map(page => (
            <TabsTrigger key={page.key} value={page.key} className="flex items-center gap-2">
              <page.icon className="w-4 h-4" />
              {page.title}
            </TabsTrigger>
          ))}
        </TabsList>

        {LEGAL_PAGES.map(page => (
          <TabsContent key={page.key} value={page.key}>
            <Card className="bg-white rounded-3xl shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
                    <page.icon className="w-6 h-6 text-[#C41E3A]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{page.title}</h3>
                    <p className="text-sm text-slate-500">Edit content for all languages</p>
                  </div>
                </div>

                {/* Language Tabs */}
                <Tabs value={activeLang} onValueChange={setActiveLang}>
                  <TabsList className="grid w-full grid-cols-3 mb-4">
                    {LANGUAGES.map(lang => (
                      <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                        {lang.flag} {lang.name}
                        {content[`${page.key}_${lang.code}`] && <span className="ml-1 text-green-500">✓</span>}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {LANGUAGES.map(lang => (
                    <TabsContent key={lang.code} value={lang.code}>
                      <div className={`p-4 rounded-2xl ${lang.bg}`}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className={`font-bold ${lang.text}`}>{lang.flag} {lang.name}</h4>
                          <span className="text-xs text-slate-500">
                            {(content[`${page.key}_${lang.code}`] || '').length} characters
                          </span>
                        </div>
                        
                        {/* Page Title */}
                        <div className="mb-4">
                          <Label className="text-sm font-medium mb-1 block">Page Title ({lang.name})</Label>
                          <Input
                            value={titles[`${page.key}_title_${lang.code}`] || ''}
                            onChange={(e) => updateTitle(page.key, lang.code, e.target.value)}
                            placeholder={lang.code === 'de' ? page.titleDe : page.title}
                            className="bg-white"
                          />
                        </div>
                        
                        {/* Content */}
                        <Label className="text-sm font-medium mb-1 block">Content ({lang.name})</Label>
                        <Textarea
                          value={content[`${page.key}_${lang.code}`] || ''}
                          onChange={(e) => updateContent(page.key, lang.code, e.target.value)}
                          className="bg-white min-h-[400px] font-mono text-sm"
                          placeholder={`Enter ${page.title} content in ${lang.name}...

You can use Markdown formatting:
# Heading 1
## Heading 2
### Heading 3

**Bold text**
*Italic text*

- Bullet point
- Another point

1. Numbered list
2. Second item

[Link text](https://example.com)
`}
                        />
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Quick Preview */}
      <Card className="bg-slate-50 rounded-3xl">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <FileText className="w-4 h-4" />
            <span>Tip: Legal content supports Markdown formatting. Use # for headings, ** for bold, * for italic, and - for bullet points.</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
